import { Component, OnInit, Inject, PLATFORM_ID, OnDestroy, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { ClientService } from '../../core/services/client.service';
import { ActivatedRoute, Router } from '@angular/router';
import { trigger, transition, animate, keyframes, style } from '@angular/animations';
import { SeoService } from '../../core/services/seo.service';
import { UserService } from '../../core/services/user.service';
import { isPlatformBrowser, Location } from '@angular/common';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { ToolsService } from '../../core/services/tools.service';
import { environment } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { IntlService } from '../../core/services/intl.service';
import { AnalyticsService } from '../../core/services/analytics.service';

declare var WebFont: any;
declare const fbq;

const MERCHANT_KEY = makeStateKey('merchant');
declare var FB: any;

@Component({
  selector: 'app-client-page',
  templateUrl: './client-page.component.html',
  styleUrls: ['./client-page.component.scss'],
  animations: [
    trigger('flyInOut', [
      transition(
        'void => *',
        animate(360, keyframes([style({ opacity: '0', top: '10px', offset: 0 }), style({ opacity: '1', top: '0px', offset: 1 })]))
      ),
    ]),
  ], // animations
})
export class ClientPageComponent implements OnInit, OnDestroy {
  @Input() public editorPreviewMode = false;
  @Output() public clientPagePreviewLoaded: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('cardBody', { static: false }) cardBody: ElementRef;
  public loading = true;
  public currentPage;
  public currentStoreSlug;

  public currentUserObservable;

  public isAdmin = false;
  public skipIsAdmin = false;

  public urlCopySuccess = false;

  public merchant;
  public selectedStore;
  public selectedServices;
  public selectedProviders = [];
  public selectedDay;
  public selectedTime;
  public finalBooking;
  public pageType = 'newBooking';
  public preselectedProviderOrStoreSlug;
  public preselectedProvider;
  public preselectedStore;
  public preselectedServiceId;
  public host;


  public showHeader = true;
  public showFooter = true;
  public isWordpress = false;

  public isBrowser;
  public role;

  public wordpressResizeInterval;

  public powerdByLink: string;

  public merchantUuid: string;

  public googleReserveToken: string;
  public selectedStoreAddress: string;
  public multipleStores = false;

  constructor(
    @Inject(PLATFORM_ID) protected platformId,
    private clientService: ClientService,
    private userService: UserService,
    private state: TransferState,
    private activatedRoute: ActivatedRoute,
    private toolsService: ToolsService,
    private router: Router,
    private seoService: SeoService,
    public translate: TranslateService,
    private intlService: IntlService,
    private analyticsService: AnalyticsService,
    private location: Location
  ) {}

  ngOnInit() {
    this.host = this.intlService.getHost();
    this.powerdByLink = environment.domains.find((v) => v.host === this.intlService.getHost())?.host;

    this.isBrowser = isPlatformBrowser(this.platformId);

    if (this.activatedRoute.snapshot.data.type) {
      this.pageType = this.activatedRoute.snapshot.data.type;
    }

    if (this.activatedRoute.snapshot.params.providerOrStoreSlug) {
      this.preselectedProviderOrStoreSlug = this.activatedRoute.snapshot.params.providerOrStoreSlug;
    }
    if (this.activatedRoute.snapshot.params.uuid) {
      this.merchantUuid = this.activatedRoute.snapshot.params.uuid;
    }

    if (this.activatedRoute.snapshot.params.serviceId) {
      this.preselectedServiceId = this.activatedRoute.snapshot.params.serviceId;
    }

    if (this.activatedRoute.snapshot.queryParams.header && this.activatedRoute.snapshot.queryParams.header === 'false') {
      this.showHeader = false;
    }

    if (this.activatedRoute.snapshot.queryParams.rwg_token?.length > 0) {
      this.googleReserveToken = this.activatedRoute.snapshot.queryParams.rwg_token;
    }

    if (this.activatedRoute.snapshot.queryParams.footer && this.activatedRoute.snapshot.queryParams.footer === 'false') {
      this.showFooter = false;
    }
    if (this.activatedRoute.snapshot.queryParams.embed && this.activatedRoute.snapshot.queryParams.embed === 'true') {
      this.showFooter = false;
      this.showHeader = false;
      this.skipIsAdmin = true;
    }
    if (this.activatedRoute.snapshot.queryParams.wordpress && this.activatedRoute.snapshot.queryParams.wordpress === 'true') {
      this.isWordpress = true;
      if (this.isBrowser) {
        this.wordpressResizeInterval = setInterval(() => {
          window.top.postMessage({ name: 'buk_wordpress', height: this.cardBody?.nativeElement?.offsetHeight }, '*');
        }, 250);
      }
    }

    const serverState = this.state.get(MERCHANT_KEY, null as any);
    if (!serverState) {
        this.getMerchant();
    } else {
      this.initPage(serverState);
    }

    this.activatedRoute.params.subscribe((resRoute) => {
      this.currentStoreSlug = resRoute.slug;
    });
    if (this.isBrowser) {
      const fbInitParams = {
        appId: environment.facebookApp.id,
        xfbml: true,
        version: environment.facebookApp.version,
      };
      if (typeof FB !== 'undefined') {
        FB.init(fbInitParams);
      }
    }
  }

  getMerchant() {
      const payload = this.preselectedProviderOrStoreSlug ? { providerOrStoreSlug: this.preselectedProviderOrStoreSlug } : {};
      if (this.preselectedServiceId) {
        payload['serviceId'] = this.preselectedServiceId;
      }
      let apiCall;
      let identifier;
      if (this.merchantUuid) {
        identifier = this.merchantUuid;
        apiCall = this.clientService.getMerchantByUuid(identifier, payload);
      } else {
        identifier = this.activatedRoute.snapshot.params.slug;
        apiCall = this.clientService.getMerchant(identifier, payload);
      }

      apiCall.subscribe(
        (res) => {
          if (!res.activeBookingPage && !this.editorPreviewMode) {
            this.analyticsService.trackEvent('ClientPageNotFound', 'PageNotActive', identifier);
            this.router.navigateByUrl('/notfound');
            this.loading = false;
            return;
          }
          this.initPage(res);
          this.state.set(MERCHANT_KEY, res as any);
        },
        () => {
          this.analyticsService.trackEvent('ClientPageNotFound', 'PageNotFound', identifier);
          this.router.navigateByUrl('/notfound');
          this.loading = false;
        }
      );
  }

  saveServices(data) {
    this.selectedServices = data;
    if (this.preselectedProvider) {
      const provider = this.selectedProviders[0];
      this.selectedProviders = [];
      while (this.selectedProviders.length < this.selectedServices.length) {
        this.selectedProviders.push(provider);
      }
      this.currentPage = this.currentPage + 2;
    } else {
      this.currentPage = this.currentPage + 1;
    }
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      this.state.remove(MERCHANT_KEY);
      this.seoService.setMobileIcons();
    }
    if (this.wordpressResizeInterval) {
      clearInterval(this.wordpressResizeInterval);
    }
  }

  initPage(res) {
    this.clientPagePreviewLoaded.emit(res);
    this.merchant = res;
    if (this.merchant.gaPropertyId) {
      this.analyticsService.trackClientPageView(this.merchant.gaPropertyId, this.intlService.getHost() ,this.merchant.slug, this.location.path());
    }
    this.loadFont(res);
    if (this.isBrowser) {
      this.checkIsAdmin(res);
    }
    this.setMetaTags(res);
    this.preselectedStore = false;
    this.preselectedProvider = false;
    if (res.storeFromSlug) {
      this.preselectedStore = true;
      this.selectedStore = res.storeFromSlug;
      this.selectedStoreAddress = this.selectedStore?.googlePlaceAddress || this.selectedStore?.address;
      this.currentPage = 2;
    } else if (res.provider) {
      this.preselectedProvider = true;
      this.selectedStore = res.provider.store;
      this.selectedProviders = [res.provider];
      this.currentPage = 2;
    } else if (this.preselectedServiceId && res.serviceFromId) {
      const serviceStore = res.stores.find((store) => store.id === res.serviceFromId.storeId && store.status === 'open' && store.servicesAvailableOnBookingPage);
      if (serviceStore) {
        this.selectedServices = [res.serviceFromId];
        this.preselectedStore = true;
        this.selectedStore = serviceStore;
        this.selectedStoreAddress = this.selectedStore?.googlePlaceAddress || this.selectedStore?.address;
        this.currentPage = 3;
      }
    } else if (res.stores.filter((store) => store.status === 'open' && store.servicesAvailableOnBookingPage).length === 1) {
      this.selectedStore = res.stores.find((store) => store.status === 'open' && store.servicesAvailableOnBookingPage);
      this.selectedStoreAddress = this.selectedStore?.googlePlaceAddress || this.selectedStore?.address;
      this.currentPage = 2;
    } else {
      this.multipleStores = true;
      this.currentPage = 1;
    }
    if (!res.provider && !res.storeFromSlug && this.pageType !== 'cancelBooking' && this.pageType !== 'payBooking' && !this.selectedServices?.length) {
      // This redirect is not wanted on pageType = cancelBooking
      this.preselectedProviderOrStoreSlug = null;
      this.preselectedStore = false;
      this.preselectedProvider = false;
      if (!this.editorPreviewMode) {
        this.router.navigate(['/' + this.merchant.slug], {queryParamsHandling: 'preserve'});
      }
    }
    this.loading = false;
    if (this.isBrowser) {
      this.setUpClientFacebookPixel(this.merchant);
    }
  }

  setUpClientFacebookPixel(merchant) {
    if (!merchant.facebookPixel) {
      return;
    }
    setTimeout(() => {
        if (typeof fbq === 'function') {
          fbq('init', merchant.facebookPixel);
          fbq('trackSingle', merchant.facebookPixel, 'PageView');
        }
    }, 2000);
  }

  setMetaTags(merchant) {
    const image = merchant.headerImage ? `${merchant.headerImage}?w=1200` : this.getDefaultBgImage();
    const url = `https://${merchant.slug}.${this.host}`;

    this.translate.get(['CLIENT.clientPage.seoTitle', 'CLIENT.clientPage.seoDescription']).subscribe((trans) => {
      this.seoService.setMetaTags(
        `${merchant?.settings?.pageHeader ||  merchant.name} | ` + trans['CLIENT.clientPage.seoTitle'],
        trans['CLIENT.clientPage.seoDescription'],
        image,
        url
      );
    });

    this.seoService.setMobileIcons(merchant.headerImage);
  }

  loadFont(merchant) {
    if (isPlatformBrowser(this.platformId)) {
      if (merchant.settings && merchant.settings.font) {
        WebFont?.load({ google: { families: [merchant.settings.font] } });
      }
    }
  }

  showBackBtn() {
    if (!this.merchant) {
      return;
    }
    if ((this.preselectedStore || this.preselectedProvider) && this.currentPage === 2) {
      return false;
    }
    const activeStoresCount = this.merchant.stores.filter((v) => v.status === 'open' && v.servicesAvailableOnBookingPage).length;
    if (activeStoresCount === 1 && this.currentPage > 2) {
      return true;
    }
    if (activeStoresCount > 1 && this.currentPage > 1) {
      return true;
    }
    return false;
  }

  checkIsAdmin(merchant) {
    if (this.skipIsAdmin) {
      this.isAdmin = false;
      return;
    }
    this.currentUserObservable = this.userService.currentUser.subscribe(
      (res) => {
        this.isAdmin = res && res.merchantSlug === merchant.slug;
        this.role = res && res.role;
      },
      () => {
        this.isAdmin = false;
      }
    );
  }

  adminUpdateMerchant(merchant, softUpdate = false) {
    const previouseSlug = this.merchant.slug;
    this.merchant = merchant;
    if (softUpdate) {
      this.merchant.slug = previouseSlug;
    }
    this.setMetaTags(merchant);
  }

  urlCopied() {
    this.urlCopySuccess = true;
    setTimeout(() => {
      this.urlCopySuccess = false;
    }, 1000);
  }

  socialShare(medium) {
    const SHARE_URL = 'https://' + this.currentStoreSlug + '.' + this.host;
    const TEXT = `${this.merchant.name} | ` + this.translate.instant('CLIENT.clientPage.seoTitle');

    switch (medium) {
      case 'fb':
        FB.ui({
          method: 'share',
          href: SHARE_URL,
        });
        break;

      case 'messenger':
        FB.ui({
          method: 'send',
          link: SHARE_URL,
        });
        break;

      case 'whatsapp':
        window.open(
          'https://api.whatsapp.com/send?text=' + encodeURIComponent(SHARE_URL) + ' ' + encodeURIComponent(TEXT),
          '',
          'left=0,top=0,width=550,height=450,personalbar=0,toolbar=0,scrollbars=0,resizable=0'
        );
        break;

      case 'messenger:mobile':
        window.open(
          'fb-messenger://share?link=' + encodeURIComponent(SHARE_URL) + '&app_id=' + encodeURIComponent(environment.facebookApp.id)
        );
        break;

      case 'twitter':
        window.open(
          'http://twitter.com/share?url=' + encodeURIComponent(SHARE_URL) + '&text=' + encodeURIComponent(TEXT),
          '',
          'left=0,top=0,width=550,height=450,personalbar=0,toolbar=0,scrollbars=0,resizable=0'
        );
        break;

      default:
        break;
    }
  }

  getDefaultBgImage() {
    return this.toolsService.getDefaultBgImage(this.merchant);
  }

  getHeaderImg() {
    return this.toolsService.getHeaderImg(this.merchant);
  }
}
